import React from "react";
import NodeGlyph from "../revenue-path/NodeGlyph";

const LEAKS = [
  {
    state: "leak",
    title: "Missed inquiries",
    body: "Leads come in, but no one sees them fast enough to move them forward.",
  },
  {
    state: "friction",
    title: "Slow follow-up",
    body: "The prospect is ready now, but the business responds hours or days later.",
  },
  {
    state: "broken",
    title: "Scattered DMs, forms, calls, and texts",
    body: "The owner has no clean view of where demand is coming from.",
  },
  {
    state: "friction",
    title: "Manual booking",
    body: "Every appointment depends on someone remembering to send the link.",
  },
  {
    state: "broken",
    title: "No payment handoff",
    body: "Interest turns into conversation, but not always into collected revenue.",
  },
  {
    state: "leak",
    title: "No review or reactivation flow",
    body: "Past clients and happy customers are left untouched.",
  },
];

const STATE_LABEL = {
  leak: "Leak",
  friction: "Friction",
  broken: "Broken",
};

/** Problem section — the six revenue leaks that sit inside a typical lead flow. */
export default function ProblemLeaks() {
  return (
    <section
      data-testid="problem-leaks-section"
      className="bg-sand-100 px-5 sm:px-6 md:px-10 py-16 sm:py-20 md:py-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end mb-10 sm:mb-12">
          <div className="lg:col-span-7 reveal">
            <p className="eyebrow">The Revenue Leak</p>
            <h2
              data-kinetic
              className="font-display mt-4 sm:mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.1rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
            >
              There&rsquo;s revenue hiding{" "}
              <span className="italic font-normal text-ink-muted">
                inside your lead flow.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-base md:text-lg text-ink-muted leading-relaxed">
              Most service businesses do not need more random tools. They need
              the moments between interest, follow-up, booking, payment,
              reviews, and reactivation connected into one clean system.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {LEAKS.map((leak, idx) => (
            <article
              key={leak.title}
              data-testid={`leak-card-${idx}`}
              className="reveal group relative border-t border-sand-300 bg-sand-50 p-6 sm:p-7 transition-colors hover:border-t-clay"
            >
              <span className="absolute -top-px left-0 h-px w-0 bg-clay transition-all duration-500 group-hover:w-16" />
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
                  Leak 0{idx + 1}
                </span>
                <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-clay-deep">
                  {STATE_LABEL[leak.state]}
                  <NodeGlyph state={leak.state} size={18} />
                </span>
              </div>
              <h3 className="font-display mt-5 text-lg sm:text-xl font-semibold tracking-tight text-ink">
                {leak.title}
              </h3>
              <p className="mt-2.5 text-[14.5px] leading-relaxed text-ink-muted">
                {leak.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
